// AppShell — 7shifts design language:
// - Sidebar: Ink Black surface, fixed, rounded-r-[40px] (large element radius).
// - Активный пункт навигации: Blueprint Blue pill.
// - Top bar в main: Whisper White pill selectors (tenant + branch).
// Структура по wireframes.md «Глобальная навигация».

import {
  Bell,
  Bug,
  Calendar,
  CircleHelp,
  CircleDollarSign,
  ClipboardCheck,
  ClipboardList,
  Home,
  KanbanSquare,
  Workflow,
  Library,
  IdCard,
  ListTodo,
  LogOut,
  MessageSquare,
  PanelLeft,
  Palette,
  PhoneCall,
  Settings,
  ShieldCheck,
  Smartphone,
  Ticket,
  Users,
  Upload,
  Warehouse,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { OpenShiftReminder } from '@/components/OpenShiftReminder';
import { UpdateBanner } from '@/components/UpdateBanner';
import { AssistantWidget } from '@/features/assistant/components/AssistantWidget';
import { useBranches } from '@/features/clients/hooks/useClients';
import { useMessengerSummary } from '@/features/messenger/hooks/useMessenger';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { UserMenu } from '@/features/profile/components/UserMenu';
import { useCities } from '@/features/reference/hooks/useReference';
import { REPORT_TAB_ACTIONS } from '@/features/reports/lib/report-tabs';
import { BarcodeQuickEnroll } from '@/features/schedule/components/BarcodeQuickEnroll';
import { useAuth, useCan } from '@/lib/auth-context';
import { branchSelectionLabel } from '@/lib/branch-label';
import { useTenant } from '@/lib/tenant-context';
import { cn } from '@/lib/utils';

import { BranchMultiSelect } from './BranchMultiSelect';
import { ChangePasswordGate } from './ChangePasswordGate';
import { MobileNav, type MobileNavItem } from './MobileNav';
import { PullToRefresh } from './PullToRefresh';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  /**
   * Право, открывающее пункт (см. Action в rbac.service.ts). undefined —
   * пункт виден всем авторизованным. Видимость меню = права из /auth/me
   * (роль + совмещение + overrides «Доступа») — роли в UI НЕ хардкодим.
   */
  action?: string;
  /**
   * Раздел открывается ЛЮБЫМ из прав (батч №13): у «Отчётов» каждая вкладка
   * закрыта своим правом, и пункт меню нужен, пока доступна хоть одна.
   */
  anyOfActions?: readonly string[];
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { to: '/dashboard', label: 'Дашборд', icon: Home, action: 'reports:dashboard' },
  { to: '/schedule', label: 'Расписание', icon: Calendar, action: 'attendance:mark' },
  // «Рабочий день» (план 121 Ф2): лист смены — занятия дня и галочки «пришёл».
  // Право то же, что у расписания: отдельного attendance:read не существует, а
  // заводить новое право — менять матрицу, это решение владельца.
  { to: '/workday', label: 'Рабочий день', icon: ClipboardCheck, action: 'attendance:mark' },
  // Раздел «Контакты» (бывш. «Клиенты», docs/service/52): в терминологии
  // владельца контакт — человек, сделка — его путь внутри бизнеса. URL и API
  // остались /clients — переименование только видимое.
  { to: '/clients', label: 'Контакты', icon: Users, action: 'client:edit_basic' },
  { to: '/messenger', label: 'Мессенджер', icon: MessageSquare, action: 'messenger:read' },
  // Уведомления сотрудника (задачи/записи) — доступны всем ролям.
  { to: '/notifications', label: 'Уведомления', icon: Bell },
  { to: '/tasks', label: 'Задачи', icon: ListTodo, action: 'client:edit_basic' },
  { to: '/employees', label: 'Сотрудники', icon: IdCard, action: 'employee:manage' },
  // «Сегменты» переехали вкладкой в «Справочники» (Ф6 плана 39); роут /segments
  // остаётся живым (прямые ссылки), пункт меню убран.
  { to: '/subscriptions', label: 'Товары и услуги', icon: Ticket, action: 'product:read' },
  { to: '/cash', label: 'Касса', icon: CircleDollarSign, action: 'cashshift:open_close' },
  // Склад товаров (план 154): раздел открывает любое право goods:*. С деплоя
  // Ф4 права по ролям (ответ владельца 8): администратор и выше видят раздел.
  {
    to: '/goods',
    label: 'Склад',
    icon: Warehouse,
    anyOfActions: ['goods:sell', 'goods:receive', 'goods:write_off', 'goods:manage'],
  },
  // Сделки — НАША воронка отдела продаж (docs/service/52); read-only канбан
  // Bitrix24 из меню убран, его блок остался в карточке контакта.
  { to: '/deals', label: 'Сделки', icon: KanbanSquare, action: 'deal:read' },
  // Обзвон после пробного (план 141): список клиентов после пробного занятия.
  { to: '/trial-followup', label: 'Обзвон после пробного', icon: PhoneCall, action: 'deal:read' },
  // Автоматизация (план 73): правила меняют процесс школы, поэтому право то
  // же, что у стадий и справочников CRM, — не 'deal:manage'.
  { to: '/automation', label: 'Автоматизация', icon: Workflow, action: 'automation:manage' },
  {
    to: '/reports',
    label: 'Отчёты',
    icon: ClipboardList,
    // Пункт виден, если доступна хотя бы одна вкладка отчётов; служебное право
    // reports:branch_finance само по себе раздел не открывает.
    anyOfActions: REPORT_TAB_ACTIONS,
  },
  { to: '/reference', label: 'Справочники', icon: Library, action: 'product:manage' },
  // Импорт базы из файла (план 85): управляющий и владелец — операция разовая
  // и массовая, вместе с деньгами клиентов.
  { to: '/import', label: 'Импорт', icon: Upload, action: 'import:run' },
  // Контент мини-аппа: новости/галерея — Manager/Boss.
  { to: '/app-content', label: 'Приложение', icon: Smartphone, action: 'school_profile:manage' },
  // Витрина дизайн-системы — служебный экран, показываем только боссу.
  { to: '/design-system', label: 'Дизайн-система', icon: Palette, action: 'access:manage' },
  { to: '/access', label: 'Доступ', icon: ShieldCheck, action: 'access:manage' },
  // Телеметрия: события, ошибки, паттерны (правка владельца 2026-07-21).
  { to: '/logs', label: 'Логи и ошибки', icon: Bug, action: 'audit:read' },
  { to: '/settings', label: 'Настройки', icon: Settings, action: 'integration:errors_view' },
  // «Помощь» — обучающие уроки (план 123). БЕЗ права: гайд нужен всем ролям,
  // включая педагога, у которого прав почти нет. Внутри уроки фильтруются по
  // правам сотрудника, поэтому лишнего он там не увидит.
  { to: '/help', label: 'Помощь', icon: CircleHelp },
];

/** Пункты меню, доступные по правам (сайдбар и нижняя панель — один фильтр). */
export function visibleNavItemsFor(can: (action: string) => boolean): NavItem[] {
  return NAV_ITEMS.filter(
    (item) =>
      (!item.action || can(item.action)) &&
      (!item.anyOfActions || item.anyOfActions.some((a) => can(a))),
  );
}

/**
 * Четыре вкладки нижней панели телефона (план 110), в порядке слева направо.
 * Пятое место занимает «Ещё» — там всё остальное из NAV_ITEMS.
 *
 * Выбор — по частоте в смене администратора: расписание и мессенджер открывают
 * десятки раз в день, контакты — на каждый звонок, дашборд — точка входа. Если
 * права закрывают какой-то из четырёх, его место занимает следующий доступный:
 * дыр в панели не бывает.
 */
const MOBILE_PRIMARY = ['/dashboard', '/schedule', '/messenger', '/clients'] as const;

const ROLE_LABEL: Record<string, string> = {
  boss: 'Босс',
  manager: 'Управляющий',
  senior_admin: 'Старший администратор',
  admin: 'Администратор',
  sales_manager: 'Менеджер',
  teacher: 'Педагог',
};

/**
 * Layout — тонкая обёртка (Ф4 плана 38): пока не сменён временный пароль,
 * рендерим ТОЛЬКО форму смены — без AppShell с его поллерами (мессенджер,
 * колокольчик, филиалы), иначе они молотили 403 password_change_required
 * (23 события за минуту в телеметрии). Хуки живут в AppShell → не вызываются
 * до смены пароля.
 */
export function Layout(): JSX.Element {
  const { user } = useAuth();
  if (user?.mustChangePassword) {
    return (
      <div className="min-h-screen bg-canvas-white max-lg:min-h-[100dvh]">
        <ChangePasswordGate />
      </div>
    );
  }
  return <AppShell />;
}

function AppShell(): JSX.Element {
  const { user, signOut } = useAuth();
  const { tenantSlug, branchIds, branchChosen, setBranches, setDefaultBranch } = useTenant();
  const navigate = useNavigate();
  // Свёрнутый сайдбар (до иконок) — состояние переживает перезагрузку.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === '1',
  );
  // Высота РАЗВЁРНУТОЙ шапки (3 строки: название/тенант/филиал; филиал может
  // врапнуться) — измеряем фактическую и резервируем её в свёрнутом виде,
  // чтобы значки меню не прыгали вертикально (правка владельца). 92px — фолбэк
  // до первого измерения (холодный старт в свёрнутом состоянии).
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerH, setHeaderH] = useState<number>(() => {
    const saved = Number(localStorage.getItem('sidebar-header-h'));
    return Number.isFinite(saved) && saved > 0 ? saved : 92;
  });
  // Без deps: замер на каждом рендере развёрнутого состояния (гвард по
  // значению), чтобы поймать и смену филиала/тенанта, и врап строк.
  useLayoutEffect(() => {
    if (!collapsed && headerRef.current) {
      const h = headerRef.current.offsetHeight;
      if (h > 0 && h !== headerH) {
        setHeaderH(h);
        localStorage.setItem('sidebar-header-h', String(h));
      }
    }
  });

  function toggleCollapsed(): void {
    setCollapsed((v) => {
      const next = !v;
      localStorage.setItem('sidebar-collapsed', next ? '1' : '0');
      return next;
    });
  }
  // Видимость меню/бейджей — по эффективным правам из /auth/me (can),
  // роли в Layout больше не хардкодятся.
  const can = useCan();

  // Счётчик непрочитанных для бейджа на пункте «Мессенджер» (poll ~30с).
  const messengerSummary = useMessengerSummary();
  const unreadMessages = can('messenger:read') ? (messengerSummary.data?.unreadMessages ?? 0) : 0;

  // Филиалы, доступные пользователю. Пустой branchScopes = «все филиалы» (Boss).
  const branchesQuery = useBranches();
  const allBranches = branchesQuery.data ?? [];
  const scopes = user?.branchScopes ?? [];
  const visibleBranches =
    scopes.length > 0 ? allBranches.filter((b) => scopes.includes(b.id)) : allBranches;
  const selectedBranchName = branchSelectionLabel(
    branchIds
      .map((id) => allBranches.find((b) => b.id === id))
      .filter((b): b is NonNullable<typeof b> => Boolean(b)),
  );
  // Имена городов для группировки в выпадашке (план 106): у Мурманска два
  // филиала, и «выбрать город целиком» — главный сценарий владельца.
  const citiesQuery = useCities();
  const cityNames = new Map((citiesQuery.data ?? []).map((c) => [c.id, c.name]));

  // Дефолтный филиал сотрудника (правка владельца): привязанный к филиалу видит
  // отчёты и расписание сразу по нему, а не по «Всем филиалам». Подставляем один
  // раз — если пользователь ещё не выбирал филиал сам (см. branchChosen).
  useEffect(() => {
    if (branchChosen || branchIds.length > 0) return;
    if (scopes.length === 0) return; // Boss без скоупа — «Все филиалы» и есть его дефолт.
    const first = visibleBranches[0];
    if (first) setDefaultBranch(first.id);
  }, [branchChosen, branchIds.length, scopes.length, visibleBranches, setDefaultBranch]);
  const tenantName = user?.tenantName ?? tenantSlug ?? 'tenant: не определён';

  function handleSignOut(): void {
    void signOut().then(() => navigate('/login', { replace: true }));
  }

  // Пункты для нижней панели — ИЗ ТОГО ЖЕ списка и с тем же фильтром прав, что
  // у сайдбара: два меню с разными наборами разъехались бы при первой правке
  // RBAC (правило «новые кнопки гейтить только can()»).
  const visibleNavItems = visibleNavItemsFor(can);
  const mobileNavItems: MobileNavItem[] = visibleNavItems.map((item) => ({
    to: item.to,
    label: item.label,
    icon: item.icon,
    ...(item.to === '/messenger' && unreadMessages > 0 ? { badge: unreadMessages } : {}),
  }));
  const mobileNavFooter = user ? (
    <div className="space-y-1">
      <div className="text-sm font-medium text-ink-black">{user.fullName}</div>
      <div className="text-caption text-muted-text">{ROLE_LABEL[user.role] ?? user.role}</div>
      <button
        type="button"
        onClick={handleSignOut}
        className="mt-3 flex min-h-[44px] items-center gap-2 rounded-full px-3 text-sm text-muted-text hover:bg-whisper-white"
      >
        <LogOut className="h-4 w-4" />
        Выход
      </button>
    </div>
  ) : null;

  return (
    // Динамическая высота на телефоне: 100vh в мобильном браузере и в
    // установленном приложении БОЛЬШЕ видимой области экрана — страница
    // получалась выше, чем видно, и под нижней панелью оставалась
    // прокручиваемая пустота (жалоба владельца 28.08). dvh считает ровно
    // видимую часть.
    <div className="min-h-screen bg-canvas-white max-lg:min-h-[100dvh]">
      {/* Sidebar: Ink Black, fixed, скруглённый правый край. Свёрнутый → 80px (иконки). */}
      <aside
        className={cn(
          // max-lg: сайдбара на телефоне нет вовсе — вместо него нижняя
          // панель (план 110). Десктоп не меняется ни на пиксель.
          'fixed left-0 top-0 z-30 hidden h-screen flex-col rounded-r-[40px] bg-ink-black text-canvas-white transition-[width] lg:flex',
          collapsed ? 'w-20 px-3 py-6' : 'w-64 p-6',
        )}
      >
        {/* Шапка держит ОДИНАКОВУЮ высоту в обоих состояниях (min-h-[68px]) —
            иначе значки меню прыгают вертикально при сворачивании (3 строки
            текста против одной «DS»). Запрос владельца: положение значков
            относительно раскрытого формата сохраняется. */}
        <div className="flex min-h-[92px] items-start justify-between pb-6">
          {collapsed ? (
            <div className="text-heading-sm font-medium leading-tight text-canvas-white">DS</div>
          ) : (
            <div className="sidebar-fade-in">
              <div className="text-heading-sm font-medium leading-tight text-canvas-white">
                Dance School CRM
              </div>
              <div className="mt-2 text-caption text-canvas-white/60">{tenantName}</div>
              <div className="mt-1 text-caption">
                <span className="text-canvas-white/60">Филиал:</span>{' '}
                <span className="font-medium text-canvas-white">{selectedBranchName}</span>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            className="shrink-0 rounded-full p-1 text-canvas-white/60 hover:bg-white/10 hover:text-canvas-white"
          >
            <PanelLeft className="h-4 w-4" />
          </button>
        </div>

        {/* min-h-0 + overflow-y-auto: пунктов 16, и на невысоком экране (ноутбук,
            масштаб 125%) нижние — «Доступ», «Настройки» — просто обрезались, до
            них было не добраться (жалоба владельца 2026-07-20: «страницы доступов
            не видно»). Шапка и блок пользователя внизу остаются на месте,
            скроллится только список. */}
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-full py-2 text-sm font-medium transition-colors',
                    // Свёрнуто: НЕ центрируем, а держим ту же левую кромку иконки, что в
                    // развёрнутом (40px от края окна): aside px-3 (12px) + pl-7 (28px).
                    // Так иконки не прыгают горизонтально при сворачивании.
                    collapsed ? 'justify-start pl-7 pr-0' : 'px-4',
                    isActive
                      ? 'bg-blueprint-blue text-canvas-white'
                      : 'text-canvas-white/70 hover:text-canvas-white hover:bg-white/5',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!collapsed ? <span className="sidebar-fade-in flex-1">{item.label}</span> : null}
                {item.to === '/messenger' && unreadMessages > 0 && (
                  <span
                    className={cn(
                      'inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-soft-orange px-1.5 text-[11px] font-semibold text-canvas-white',
                      collapsed ? 'absolute right-2' : '',
                    )}
                  >
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="pt-4">
          {user ? (
            collapsed ? (
              <button
                type="button"
                onClick={handleSignOut}
                title="Выход"
                aria-label="Выход"
                className="flex w-full items-center justify-center rounded-full py-2 text-canvas-white/70 hover:bg-white/5 hover:text-canvas-white"
              >
                <LogOut className="h-4 w-4" />
              </button>
            ) : (
              <div className="sidebar-fade-in space-y-1">
                <div className="text-sm font-medium text-canvas-white">{user.fullName}</div>
                <div className="text-caption text-canvas-white/60">
                  {ROLE_LABEL[user.role] ?? user.role}
                </div>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="mt-3 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs text-canvas-white/70 hover:bg-white/5 hover:text-canvas-white transition-colors"
                >
                  <LogOut className="h-3 w-3" />
                  Выход
                </button>
              </div>
            )
          ) : (
            <div className="text-caption text-canvas-white/60">Не авторизован</div>
          )}
        </div>
      </aside>

      {/* Main: 256px left margin (под fixed sidebar). width:auto + min-w-0 →
          колонка контента заполняет всё оставшееся пространство viewport на любой
          ширине (флюид), а min-w-0 позволяет широким таблицам со своим
          overflow-x-auto корректно сжиматься без горизонтального overflow. */}
      <main
        className={cn(
          // На телефоне отступа под сайдбар нет, зато снизу — место под
          // навигационную панель (52px + safe-area), иначе последняя строка
          // любого списка уезжает под неё.
          //
          // Динамическая высота вместо 100vh: в установленном приложении и в
          // мобильном браузере 100vh БОЛЬШЕ видимой области, и внизу оставалась
          // пустая полоса, которую приходилось прокручивать (жалоба владельца
          // 28.08).
          // Высоту в экран держит РОДИТЕЛЬ; здесь на телефоне её снимаем —
          // два вложенных «минимум на весь экран» вместе с нижним отступом
          // делали документ выше видимой области.
          'min-h-screen min-w-0 bg-canvas-white transition-[margin] max-lg:ml-0 max-lg:min-h-0 max-lg:pb-[calc(52px+env(safe-area-inset-bottom))]',
          collapsed ? 'ml-20' : 'ml-64',
        )}
      >
        {/* Top bar — Whisper White pill selectors. */}
        {/* sticky top-0: шапка остаётся на месте при прокрутке (запрос
            владельца 28.08). На телефоне это ещё и щит для статус-бара — без
            неё содержимое уезжало под часы и значки сети. Свой фон обязателен:
            прозрачная липкая шапка пропускала бы текст под собой.

            min-w-0 — но БЕЗ обрезки содержимого: она срезала выпадающие
            списки шапки, они позиционируются внутри неё (жалоба владельца
            28.08 «нажимаю филиалы, список не появляется»). Боковой скролл
            закрыт на уровне html/body в globals.css, обрезать шапку не нужно.

            pt-[env(safe-area-inset-top)]: при `viewport-fit=cover` контент
            может начинаться ПОД часами и значками сети — шапка оказывалась под
            ними и не нажималась вовсе (жалоба владельца 28.08). С 06.10
            установленное приложение под часы не заходит вовсе (index.html, без
            black-translucent): на iOS 26.1+ этот инсет там равен нулю и шапку
            не спасал. Отступ остаётся страховкой для вкладки браузера. */}
        <div className="sticky top-0 z-20 flex min-w-0 items-center gap-3 border-b border-divider-gray bg-canvas-white px-6 py-4 max-lg:gap-2 max-lg:px-3 max-lg:py-2 max-lg:pt-[calc(1.25rem+env(safe-area-inset-top))] max-lg:pb-3">
          {/* Название школы на телефоне прячем: тенант у человека один, а
              место в шапке дороже. */}
          <div className="inline-flex h-9 min-w-0 items-center gap-2 rounded-full bg-whisper-white px-4 text-sm font-medium text-ink-black max-lg:hidden">
            <span className="text-muted-text">Школа:</span>
            <span className="max-w-[20rem] truncate">{tenantName}</span>
          </div>
          <BranchMultiSelect
            branches={visibleBranches}
            cityNames={cityNames}
            selected={branchIds}
            onChange={setBranches}
          />
          {/* Скан карты на ресепшене — отмечает ПРИХОД (правка владельца
              2026-07-29), поэтому право «Отметка посещений», а не «Запись». */}
          {can('attendance:mark') ? <BarcodeQuickEnroll /> : null}
          {/* Правый край топбара: колокольчик + меню пользователя (часы + аватар).
              Колокольчик — всем: уведомления сотрудника не зависят от роли. */}
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <UserMenu />
          </div>
        </div>

        {/* Без своего overflow: страницу прокручивает окно целиком. */}
        <div className="px-6 py-6 max-lg:px-3 max-lg:py-4">
          {/* Напоминание про кассовую смену — над содержимым любого раздела:
              без открытой смены администратор не сможет продать (план 81). */}
          <OpenShiftReminder />
          <Outlet />
        </div>
      </main>

      {/* Обновление протягиванием: в установленном приложении нативного жеста
          нет вовсе, и человек не понимает, свежие ли данные (план 110). */}
      <PullToRefresh />

      {/* Нижняя навигация телефона (план 110): те же пункты и те же права,
          что у сайдбара, — список один на два меню. */}
      <MobileNav items={mobileNavItems} primary={MOBILE_PRIMARY} footer={mobileNavFooter} />

      {/* Вкладка живёт на той сборке, с которой открылась, — деплой её не
          трогает. Плашка сообщает, что вышло обновление, иначе человек видит
          старый интерфейс и считает, что правка не работает. */}
      <UpdateBanner />

      {/* Ассистент «Спросить CRM» (план 125). Без гейта прав: он нужен всем
          ролям, включая педагога, — сужают доступ инструменты на сервере. */}
      <AssistantWidget />
    </div>
  );
}
